// Reine Rechenfunktionen ohne Datenbank und ohne Node-Module — Server UND Oberfläche importieren dieselbe Datei,
// damit die Vorschau im Browser exakt die Rechnung des Servers ist (Oberflächen-Paket, 2026-10-17).

export const VOLL_BP = 10000; // 100 % in Basispunkten

export type AnteilEingabe = { steuerArtId: string; anteilBp: number };

/**
 * Beträge der Anteile auf den Cent genau: jeder Teil gerundet, der Rest geht auf den letzten Teil → Σ Teile = Betrag immer exakt.
 * Rechnet aus dem AKTUELLEN Betrag; eine Preiskorrektur an der Position wirkt damit automatisch auf alle Teile.
 */
export function anteilBetraege(betrag: number, anteile: ReadonlyArray<{ anteilBp: number }>): number[] {
  const cent = Math.round(betrag * 100);
  const teile: number[] = [];
  let rest = cent;
  anteile.forEach((a, i) => {
    const c = i === anteile.length - 1 ? rest : Math.round((cent * a.anteilBp) / VOLL_BP);
    teile.push(c);
    rest -= c;
  });
  return teile.map((c) => c / 100);
}

/** Prüft eine Aufteilung; wirft mit verständlicher Meldung. Mindestens 2 Teile, jeder > 0 %, Σ = 100 %. */
export function pruefeAufteilung(teile: ReadonlyArray<AnteilEingabe>): void {
  if (teile.length < 2) throw new Error("Aufteilen braucht mindestens zwei Teile");
  for (const t of teile) {
    if (!Number.isInteger(t.anteilBp) || t.anteilBp <= 0 || t.anteilBp >= VOLL_BP)
      throw new Error(`Anteil ${t.anteilBp / 100} % ist ungültig (zwischen 0 und 100 %)`);
  }
  const summe = teile.reduce((s, t) => s + t.anteilBp, 0);
  if (summe !== VOLL_BP) throw new Error(`Die Anteile ergeben ${summe / 100} % statt 100 %`);
}

/**
 * Effektiver Betrag je Position = Preis − Positionsrabatt − anteiliger Bon-Rabatt, auf den Cent.
 * Der Bon-Rabatt wird nach Preisanteil verteilt (wie bisher in Tabelle, Auswertungen und Export); jeder Anteil wird auf den
 * Cent gerundet und der Rundungsrest geht auf die Position mit der höchsten Positionsnummer → Σ effektive Beträge =
 * Σ Preise − Σ Positionsrabatte − Bon-Rabatt exakt. Die Reihenfolge der Eingabe spielt keine Rolle (intern nach `position`
 * sortiert), damit Detailseite, Auswertungen und Export den Rest immer derselben Position geben. Ausgabe in Eingabe-Reihenfolge.
 */
export function effektiveBetraege(
  // bonRabattAnteil ist Pflicht (auch null), damit jeder Aufrufer bewusst entscheidet — fehlt es, meldet tsc den Aufrufer.
  items: ReadonlyArray<{ position: number; priceGross: number; productDiscount: number; bonRabattAnteil: number | null }>,
  totalDiscountGross: number,
): { alloc: number; eff: number }[] {
  // 18.10.2026 („gedruckt = Wahrheit“): Positionen mit GEDRUCKTEM Coupon-Anteil (z. B. Lidl „Lidl Plus Rabatt“) behalten ihn.
  // Nur der REST des Bon-Rabatts wird auf die übrigen Positionen verteilt, nach (Brutto − Positionsrabatt) — so verteilt auch Lidl.
  // Haben alle Positionen einen Anteil und bleibt ein Rest (Rundung, Kopf von Hand geändert), geht er auf die letzte Position.
  const rabattCent = Math.round((totalDiscountGross ?? 0) * 100);
  const reihenfolge = items.map((_, i) => i).sort((a, b) => items[a]!.position - items[b]!.position || a - b);
  const allocCent = new Array<number>(items.length).fill(0);
  let rest = rabattCent;
  items.forEach((it, i) => {
    if (it.bonRabattAnteil !== null && it.bonRabattAnteil !== undefined) {
      allocCent[i] = Math.round(it.bonRabattAnteil * 100);
      rest -= allocCent[i]!;
    }
  });
  if (rest !== 0) {
    const offen = reihenfolge.filter((i) => items[i]!.bonRabattAnteil === null || items[i]!.bonRabattAnteil === undefined);
    const basis = (i: number) => Math.max(0, Math.round(items[i]!.priceGross * 100) - Math.round(items[i]!.productDiscount * 100));
    const basisSumme = offen.reduce((s, i) => s + basis(i), 0);
    if (offen.length > 0 && basisSumme > 0) {
      let r = rest;
      offen.forEach((idx, k) => {
        const c = k === offen.length - 1 ? r : Math.round((rest * basis(idx)) / basisSumme);
        allocCent[idx] = allocCent[idx]! + c;
        r -= c;
      });
    } else if (reihenfolge.length > 0) {
      const letzte = reihenfolge[reihenfolge.length - 1]!;
      allocCent[letzte] = allocCent[letzte]! + rest;
    }
  }
  return items.map((it, i) => ({
    alloc: allocCent[i]! / 100,
    eff: (Math.round(it.priceGross * 100) - Math.round(it.productDiscount * 100) - allocCent[i]!) / 100,
  }));
}

/**
 * DIE eine Lesefunktion der Steuer-Rechnung: Betrag je Buchungsart über beliebig viele Positionen.
 * Position mit Anteilen → Teilbeträge je Art; sonst der ganze Betrag auf die Art der Position (null = ohne Buchungsart).
 * `betrag` liefert der Aufrufer (z. B. effektiver Preis nach Rabatten), damit hier keine zweite Rabatt-Logik entsteht.
 */
export function betragJeBuchungsart(
  items: ReadonlyArray<{ id: string; betrag: number; steuerArtId: string | null }>,
  anteile: ReadonlyMap<string, ReadonlyArray<{ steuerArtId: string; anteilBp: number }>>,
): Map<string | null, number> {
  const cent = new Map<string | null, number>();
  const add = (art: string | null, b: number) => cent.set(art, (cent.get(art) ?? 0) + Math.round(b * 100));
  for (const it of items) {
    const teile = anteile.get(it.id);
    if (teile && teile.length > 0) {
      anteilBetraege(it.betrag, teile).forEach((b, i) => add(teile[i]!.steuerArtId, b));
    } else {
      add(it.steuerArtId, it.betrag);
    }
  }
  return new Map([...cent].map(([k, v]) => [k, v / 100]));
}
